import React, {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import {ArrowLeft, Globe, Warning} from '@phosphor-icons/react';
import {SigninForm} from '../molecules/SigninForm';
import {NEUGENCE_API_BASE, bearerHeaders, fmtNumber, PERIOD_OPTIONS, useSortableTable, SortHeader} from './_lib';

export default function DomainHealth() {
	const navigate = useNavigate();
	const [period, setPeriod] = useState('30d');
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [filter, setFilter] = useState('all'); // 'all' | 'block' | 'low'
	const signedIn = typeof window !== 'undefined' && !!sessionStorage.getItem('signedin');

	const load = useCallback(async () => {
		setLoading(true); setError(null);
		try {
			const headers = await bearerHeaders();
			const res = await axios.get(`${NEUGENCE_API_BASE}/api/v1/domain-health?period=${period}`, {headers});
			setData(res.data || {});
		} catch (e) { setError(e.message); }
		finally { setLoading(false); }
	}, [period]);

	useEffect(() => { if (signedIn) load(); }, [load, signedIn]);

	// Hooks must run on every render — keep them above the early return.
	const rows = (data && data.rows) || [];
	const visible = filter === 'all' ? rows : rows.filter(r => r.flag === filter);
	const flags = rows.reduce((acc,r) => { if (r.flag) acc[r.flag] = (acc[r.flag]||0)+1; return acc; }, {});
	const sort = useSortableTable(visible, 'send', 'desc');

	if (!signedIn) return <div style={{marginTop: 300}}><SigninForm/></div>;

	return (
		<div className="min-h-screen bg-white text-left">
			<div className="max-w-6xl mx-auto px-6 py-10">
				<button onClick={() => navigate('/dashboard')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6">
					<ArrowLeft size={14}/> Dashboard
				</button>
				<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
					<div>
						<h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-3">
							<Globe size={28} weight="duotone" color="#10b981"/> Domain health
						</h1>
						<p className="text-sm text-slate-500 mt-1">Per-recipient-domain delivery + engagement. Spot corporate gateways that block.</p>
					</div>
					<div role="tablist" className="inline-flex rounded-full bg-slate-100 p-1">
						{PERIOD_OPTIONS.map(p => (
							<button key={p.key} onClick={() => setPeriod(p.key)}
								className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${period===p.key?'bg-white text-slate-900 shadow-sm':'text-slate-600 hover:text-slate-900'}`}>{p.label}</button>
						))}
					</div>
				</div>

				<div className="flex flex-wrap gap-2 mb-6 text-xs">
					<button onClick={() => setFilter('all')} className={`rounded-full px-3 py-1 ${filter==='all'?'bg-slate-200 border border-slate-300':'bg-slate-100 border border-slate-200'}`}>all <strong>{rows.length}</strong></button>
					<button onClick={() => setFilter('block')} className={`rounded-full px-3 py-1 text-rose-800 ${filter==='block'?'bg-rose-100 border border-rose-300':'bg-rose-50 border border-rose-200'}`}>blocked <strong>{flags.block || 0}</strong></button>
					<button onClick={() => setFilter('low')} className={`rounded-full px-3 py-1 text-amber-800 ${filter==='low'?'bg-amber-100 border border-amber-300':'bg-amber-50 border border-amber-200'}`}>low engagement <strong>{flags.low || 0}</strong></button>
				</div>

				{error && <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 mb-6"><Warning size={18} className="text-amber-600 inline mr-2"/>{String(error)}</div>}
				{loading && <div className="text-center text-sm text-slate-500 py-20">Loading…</div>}

				{!loading && (
					<div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
						<table className="w-full text-sm">
							<thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
								<tr>
									<SortHeader {...sort} column="domain" className="text-left px-5 py-3">Domain</SortHeader>
									<SortHeader {...sort} column="send" numeric className="text-right px-3 py-3">Sent</SortHeader>
									<SortHeader {...sort} column="delivery_rate" numeric className="text-right px-3 py-3">Deliv %</SortHeader>
									<SortHeader {...sort} column="open_rate" numeric className="text-right px-3 py-3">Open %</SortHeader>
									<SortHeader {...sort} column="click_rate" numeric className="text-right px-3 py-3">Click %</SortHeader>
									<SortHeader {...sort} column="bounce" numeric className="text-right px-3 py-3">Bounces</SortHeader>
									<SortHeader {...sort} column="flag" className="text-left px-3 py-3">Flag</SortHeader>
								</tr>
							</thead>
							<tbody>
								{sort.rows.length === 0 ? (
									<tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-slate-500">No domains match this filter.</td></tr>
								) : sort.rows.map((r,i) => (
									<tr key={r.domain} className="border-t border-slate-100 hover:bg-slate-50/40">
										<td className="px-5 py-2 text-slate-900 font-medium">{r.domain}</td>
										<td className="px-3 py-2 text-right text-slate-700">{fmtNumber(r.send)}</td>
										<td className="px-3 py-2 text-right text-slate-700">{r.delivery_rate}%</td>
										<td className="px-3 py-2 text-right text-slate-700">{r.open_rate}%</td>
										<td className="px-3 py-2 text-right text-slate-700">{r.click_rate}%</td>
										<td className="px-3 py-2 text-right text-slate-700">{r.bounce}</td>
										<td className="px-3 py-2">
											{r.flag === 'block' && <span className="rounded-full bg-rose-100 text-rose-800 px-2 py-0.5 text-xs">blocked</span>}
											{r.flag === 'low' && <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs">low</span>}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</div>
	);
}
